import { Fragment, useEffect, useState } from 'react'
import { usePage, router } from '@inertiajs/react'
import ActiveFilters from '@/Shared/SlideOvers/ActiveFilters'
import Avatar from '@/Shared/Avatar'
import axios from '@/apis/axios'
import Button from '@/Shared/Button'
import ContactPreview from '@/Pages/Contacts/Preview'
import DataTableHeader from './DataTableHeader'
import MainLayout from '@/Shared/Layouts/Main'
import Pagination from '@/Shared/Pagination'
import SearchBar from '@/Pages/Pros/SearchBar'
import { Tooltip } from '@/Shared/Tooltip'
import classNames from 'classnames'
import AccountAlerts from '../../Shared/Alerts/AccountAlerts'
import Dropdown from '../../Shared/Dropdown'
import DropdownItem from '../../Shared/DropdownItem'
import Link from '../../Shared/Link'

const ProRow = ({ administrating, pro, setContactPreviewing }) => {
  const {
    id,
    user_id,
    avatar,
    name,
    company,
    phone,
    phone_formatted,
    phone_type,
    email,
    location,
    distance,
    service,
    status,
    tier,
    deleted_at,
    has_published_lsn,
  } = pro

  const avatarSize = tier == 1 ? 16 : tier == 2 ? 12 : 10

  const onImpersonateUser = (user_id) => {
    router.post(route('admin.users.impersonate', user_id))
  }

  const rowClicked = (id) => {
    if (administrating) {
      router.visit(route('admin.pros.show', { contact: id, subpage: 'account' }))
    } else {
      axios.get(route('pros.show', id)).then((response) => {
        setContactPreviewing(response.data)
      })
    }
  }

  return (
    <tr
      className={classNames('group border-l border-r border-gray-200 hover:bg-gray-100', tier == 3 ? 'bg-gray-200/40' : 'bg-white')}
      key={id}
    >
      <td className="w-10 whitespace-nowrap py-3 pl-4 pr-4 sm:pl-6">
        <div className={`flex h-${avatarSize} w-${avatarSize} flex-shrink-0 items-center justify-center`}>
          <div>
            <Avatar contact={{ avatar: avatar, name: name }} height={`h-${avatarSize}`} width={`w-${avatarSize}`} textSize="text-base" />
          </div>
        </div>
      </td>

      <td className="cursor-pointer py-3 pr-4 sm:pr-6" onClick={() => rowClicked(id)}>
        <div className="mb-2 leading-6 sm:mb-0 sm:flex-1">
          <div className="font-medium leading-tight text-primary-500">{name}</div>
          {company && <div className="text-gray-700">{company}</div>}

          {tier === 1 && has_published_lsn && (
            <div>
              <span className="inline-flex items-center rounded bg-[#0170b7]/15 px-2.5 py-1.5 text-[0.825rem] font-medium uppercase leading-none text-[#0170b7]">
                <span className="whitespace-nowrap leading-none">Local Service Network® Member</span>
              </span>
            </div>
          )}

          {service && <div className="text-gray-700 lg:hidden">{service}</div>}
        </div>
      </td>

      <td className="hidden cursor-pointer whitespace-nowrap px-4 py-3 text-gray-700 sm:px-6 lg:table-cell" onClick={() => rowClicked(id)}>
        {service || <span className="italic text-gray-400">Not specified</span>}
      </td>

      <td className="hidden cursor-pointer whitespace-nowrap px-4 py-3 text-gray-700 md:px-6 lg:table-cell" onClick={() => rowClicked(id)}>
        <div>{location}</div>
        {distance != null && <span className="text-primary-500">{distance} miles away</span>}
      </td>

      <td
        className="hidden cursor-pointer whitespace-nowrap px-4 py-3 text-gray-700 sm:table-cell sm:px-6"
        onClick={() => rowClicked(id)}
        width="80px"
      >
        <span
          className={classNames('inline-flex items-center rounded px-2.5 py-1.5 text-sm font-medium uppercase leading-none', {
            'bg-green-500 text-white': status === 'public',
            'bg-blue-200/40 text-blue-500': status === 'internal',
            'bg-orange-500 text-white': administrating && status === 'suspended',
            'bg-red-600 text-white': administrating && status === 'deleted',
            'bg-gray-300/75 text-gray-600': !['public', 'internal', 'suspended', 'deleted'].some((s) => s.includes(status)),
          })}
        >
          <span className="whitespace-nowrap leading-none">{status === 'internal' ? 'PROs Only' : status}</span>
        </span>
      </td>

      <td className="cursor-pointer whitespace-nowrap py-3 pr-6 text-right font-medium">
        <div className="flex items-center justify-end gap-2">
          {!administrating && (
            <div className="hidden sm:flex sm:items-center sm:justify-end sm:gap-6">
              <Tooltip label={phone ? `Call ${phone_formatted}` : null} placement="bottom">
                <a
                  className={classNames(
                    'inline-flex size-8 items-center justify-center text-gray-500 outline-none transition-all duration-300 hover:text-blue-500 focus:rounded-lg focus:ring-2 focus:ring-blue-500',
                    phone ? 'group/button' : 'cursor-not-allowed opacity-30',
                  )}
                  href={phone?.phone && `tel:${phone.phone}`}
                  disabled={!phone}
                >
                  <i className="far fa-mobile text-xl leading-none group-hover/button:font-bold"></i>
                </a>
              </Tooltip>

              <Tooltip label={email ? `Email ${email}` : null} placement="bottom">
                <a
                  role="button"
                  className={classNames(
                    'inline-flex size-8 items-center justify-center text-gray-500 outline-none transition-all duration-300 hover:text-blue-500 focus:rounded-lg focus:ring-2 focus:ring-blue-500',
                    email ? 'group/button' : 'cursor-not-allowed opacity-30',
                  )}
                  href={email ? `mailto:${email}` : null}
                  disabled={!email}
                  target={email ? '_blank' : null}
                >
                  <i className="far fa-envelope text-xl leading-none group-hover/button:font-bold"></i>
                </a>
              </Tooltip>

              <Tooltip label={phone && phone_type.toLowerCase() === 'mobile' ? `Text ${phone_formatted}` : null} placement="bottom">
                <a
                  className={classNames(
                    'inline-flex size-8 items-center justify-center text-gray-500 outline-none transition-all duration-300 hover:text-blue-500 focus:rounded-lg focus:ring-2 focus:ring-blue-500',
                    phone && phone_type.toLowerCase() === 'mobile' ? 'group/button' : 'cursor-not-allowed opacity-30',
                  )}
                  href={phone?.phone && `sms:${phone.phone}`}
                  disabled={!phone || phone_type.toLowerCase() !== 'mobile'}
                >
                  <i className="far fa-comment-alt-dots text-xl leading-none group-hover/button:font-bold"></i>
                </a>
              </Tooltip>

              <Dropdown
                id="note-options"
                label={<i className="far fa-people-arrows text-xl leading-none text-gray-500 group-hover:font-bold"></i>}
                orientation="right"
                dropup
                icon
              >
                {pro.slug ? (
                  <DropdownItem href={route('pros.vcard', pro.slug)} target="_blank">
                    <div className="group/item flex items-center">
                      <i className="far fa-address-card flex size-5 shrink-0 items-center justify-center text-gray-600 hover:text-gray-700 group-hover/item:font-bold group-hover/item:text-primary-500"></i>
                      <span className="group-data-selected/item:font-semibold ml-3 truncate">Download vCard</span>
                    </div>
                  </DropdownItem>
                ) : (
                  ''
                )}

                <DropdownItem href={`${pro.permalink}/refer/email`} target="_blank" disabled={pro.status === 'private'}>
                  <div className="group/item flex items-center">
                    <i
                      className={classNames([
                        'far fa-envelope flex size-5 shrink-0 items-center justify-center',
                        pro.status === 'private'
                          ? 'text-gray-600 hover:text-gray-700 group-hover/item:font-bold group-hover/item:text-primary-500'
                          : '',
                      ])}
                    ></i>
                    <span className="group-data-selected/item:font-semibold ml-3 truncate">Refer by Email</span>
                  </div>
                </DropdownItem>

                <DropdownItem href={`${pro.permalink}/refer/text`} target="_blank" disabled={pro.status === 'private'}>
                  <div className="group/item flex items-center">
                    <i
                      className={classNames([
                        'far fa-comment-alt-dots flex size-5 shrink-0 items-center justify-center',
                        pro.status === 'private'
                          ? 'text-gray-600 hover:text-gray-700 group-hover/item:font-bold group-hover/item:text-primary-500'
                          : '',
                      ])}
                    ></i>
                    <span className="group-data-selected/item:font-semibold ml-3 truncate">Refer by Text</span>
                  </div>
                </DropdownItem>
              </Dropdown>
            </div>
          )}

          {administrating && (
            <Tooltip label={`Impersonate ${name}`} placement="bottom">
              <Button theme="icon" disabled={deleted_at} onClick={() => onImpersonateUser(user_id)}>
                <i className="fad fa-user-secret text-xl leading-none text-gray-600"></i>
              </Button>
            </Tooltip>
          )}

          <div className="ml-3 flex items-center justify-end" onClick={() => rowClicked(id)}>
            <i className="far fa-angle-right text-2xl leading-none text-gray-700"></i>
          </div>
        </div>
      </td>
    </tr>
  )
}

const ProIndex = () => {
  const { pros, filters } = usePage().props
  const activeFilters = Object.keys(filters).filter((k) => filters[k] && ['direction', 'limit', 'radius', 'sortBy'].indexOf(k) == -1)
  const [contactPreviewing, setContactPreviewing] = useState(null)
  const administrating = route().current().startsWith('admin')
  const { paginator } = pros
  const [data, setData] = useState([])
  const tier1Pros = pros.data?.filter((pro) => pro.tier == 1)
  const tier2Pros = pros.data?.filter((pro) => pro.tier == 2)

  useEffect(() => {
    const tableData = administrating ? pros.data : pros.data?.filter((pro) => pro.tier == 3)
    setData(tableData)
  }, [pros.data])

  const changeContact = (id) => {
    setContactPreviewing(null)

    setTimeout(() => {
      rowClicked(id)
    }, 300)
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-stretch justify-between bg-white px-6 py-4">
        <SearchBar />
      </div>

      <div className="space-y-2">
        {activeFilters.length > 0 && (
          <div className="px-4 xl:px-6">
            <ActiveFilters />
          </div>
        )}

        <div className="mx-auto flex w-full max-w-screen-xl flex-1 flex-col px-4 sm:px-6 lg:px-8">
          <AccountAlerts />
        </div>

        <div className="mx-4 mb-6 sm:mx-6">
          <div className="flex flex-col">
            <div className="sm:rounded">
              <div className="inline-block min-w-full align-middle">
                <div className="border-b border-t border-gray-200 shadow">
                  <table className="min-w-full divide-y divide-gray-300/60">
                    <thead>
                      <DataTableHeader sortBy={filters.sortBy} direction={filters.direction} />
                    </thead>

                    <tbody className="divide-y divide-gray-200">
                      {tier1Pros?.length > 0 && (
                        <tr>
                          <td colSpan={8}>
                            <h3 className="shadow-t mt-3 inline-block rounded-t-md bg-green-500 px-4 py-0.5 text-base font-medium uppercase text-white">
                              Active LSN Member
                            </h3>
                          </td>
                        </tr>
                      )}

                      {tier1Pros?.map((pro) => (
                        <Fragment key={pro.id}>
                          <ProRow administrating={administrating} pro={pro} setContactPreviewing={setContactPreviewing} />
                        </Fragment>
                      ))}

                      {((tier1Pros?.length > 0 && (tier2Pros?.length > 0 || data?.length > 0)) ||
                        (tier2Pros?.length > 0 && data?.length > 0)) && (
                        <tr>
                          <td colSpan={8}>
                            {tier2Pros?.length > 0 && (tier1Pros?.length > 0 || data?.length > 0) ? (
                              <h3 className="shadow-t mt-3 inline-block rounded-t-md bg-purple-200 px-4 py-0.5 text-base font-medium uppercase text-purple-800">
                                Public PROs
                              </h3>
                            ) : (
                              <span>&nbsp;</span>
                            )}
                          </td>
                        </tr>
                      )}

                      {tier2Pros?.map((pro) => (
                        <Fragment key={pro.id}>
                          <ProRow administrating={administrating} pro={pro} setContactPreviewing={setContactPreviewing} />
                        </Fragment>
                      ))}

                      {(tier1Pros?.length > 0 || tier2Pros?.length > 0) && data?.length > 0 && (
                        <tr>
                          <td colSpan={8}>
                            <h3 className="shadow-t mt-3 inline-block rounded-t-md bg-gray-300/75 px-4 py-0.5 text-base font-medium uppercase text-gray-600">
                              Private PROs
                            </h3>
                          </td>
                        </tr>
                      )}

                      {data?.map((pro) => (
                        <Fragment key={pro.id}>
                          <ProRow administrating={administrating} pro={pro} setContactPreviewing={setContactPreviewing} />
                        </Fragment>
                      ))}

                      {(!data || pros.data?.length == 0) && (
                        <tr>
                          <td colSpan="8" className="bg-gray-200 px-4 py-32 sm:px-6">
                            <div className="flex flex-col gap-2 text-center">
                              {activeFilters.length > 0 ? (
                                <Fragment>
                                  <span className="text-2xl font-semibold text-red-600">No PROs match your current filters.</span>
                                  <span className="text-lg text-gray-700">
                                    Expecting to see results? Check your filters to make sure you're not excluding results.
                                  </span>
                                </Fragment>
                              ) : (
                                <div className="mx-auto max-w-4xl">
                                  <div>
                                    <div className="text-center">
                                      <svg
                                        className="mx-auto size-12"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24px"
                                        height="24px"
                                        viewBox="0 0 24 24"
                                        version="1.1"
                                      >
                                        <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                                          <rect id="bound" x="0" y="0" width="24" height="24"></rect>
                                          <path
                                            className="fill-current text-primary-500"
                                            d="M14.2928932,16.7071068 C13.9023689,16.3165825 13.9023689,15.6834175 14.2928932,15.2928932 C14.6834175,14.9023689 15.3165825,14.9023689 15.7071068,15.2928932 L19.7071068,19.2928932 C20.0976311,19.6834175 20.0976311,20.3165825 19.7071068,20.7071068 C19.3165825,21.0976311 18.6834175,21.0976311 18.2928932,20.7071068 L14.2928932,16.7071068 Z"
                                            id="Path-2"
                                            fillRule="nonzero"
                                            opacity="0.3"
                                          ></path>
                                          <path
                                            className="fill-current text-primary-500"
                                            d="M11,16 C13.7614237,16 16,13.7614237 16,11 C16,8.23857625 13.7614237,6 11,6 C8.23857625,6 6,8.23857625 6,11 C6,13.7614237 8.23857625,16 11,16 Z M11,18 C7.13400675,18 4,14.8659932 4,11 C4,7.13400675 7.13400675,4 11,4 C14.8659932,4 18,7.13400675 18,11 C18,14.8659932 14.8659932,18 11,18 Z"
                                            id="Path"
                                            fillRule="nonzero"
                                          ></path>
                                        </g>
                                      </svg>

                                      <h2 className="mt-3 text-2xl font-semibold text-gray-900">
                                        Find the right PRO for your next service
                                      </h2>

                                      <p className="mt-1 text-lg text-gray-500">
                                        Find a PRO by searching by their name, service or location using the available search and filters.
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {pros.data?.length > 0 && <Pagination paginator={paginator} />}
          </div>
        </div>
      </div>

      <ContactPreview
        contact={contactPreviewing}
        hideFooter={!contactPreviewing?.id}
        open={contactPreviewing != null}
        onContactChanged={changeContact}
        onClosed={() => setContactPreviewing(null)}
      >
        {contactPreviewing && (
          <ContactPreview.Actions>
            {({ contact }) => (
              <Dropdown id="actions-options" label="Actions" orientation="right" size="w-64">
                <DropdownItem href={contact?.slug ? route('pros.vcard', contact.slug) : ''} target="_blank">
                  <div className="flex items-center">
                    <i className="far fa-address-card flex size-5 shrink-0 items-center justify-center text-gray-600 hover:text-gray-700 group-hover:font-bold group-hover:text-primary-500"></i>
                    <span className="group-data-selected:font-semibold ml-3 truncate">Download vCard</span>
                  </div>
                </DropdownItem>

                <DropdownItem href={`${contact.url}/refer/email`} target="_blank" disabled={!contact.status}>
                  <div className="flex items-center">
                    <i
                      className={classNames([
                        'far fa-envelope flex size-5 shrink-0 items-center justify-center',
                        contact.status ? 'text-gray-600 hover:text-gray-700 group-hover:font-bold group-hover:text-primary-500' : '',
                      ])}
                    ></i>
                    <span className="group-data-selected:font-semibold ml-3 truncate">Refer by Email</span>
                  </div>
                </DropdownItem>

                <DropdownItem href={`${contact.url}/refer/text`} target="_blank" disabled={!contact.status}>
                  <div className="flex items-center">
                    <i
                      className={classNames([
                        'far fa-comment-alt-dots flex size-5 shrink-0 items-center justify-center',
                        contact.status ? 'text-gray-600 hover:text-gray-700 group-hover:font-bold group-hover:text-primary-500' : '',
                      ])}
                    ></i>
                    <span className="group-data-selected:font-semibold ml-3 truncate">Refer by Text</span>
                  </div>
                </DropdownItem>
              </Dropdown>
            )}
          </ContactPreview.Actions>
        )}

        <ContactPreview.FooterActions>
          {({ contact }) => (
            <div className="flex justify-center">
              <Link
                theme="solid"
                className="flex items-center gap-2"
                href={contact.status === 'internal' ? route('pros.show', contact.id) : contact.url ? contact.url : null}
                disabled={!contact.status || !contact.url}
                target="_blank"
              >
                {contact.status === 'internal' ? 'View Internal' : 'View'} PRO-Website
                <i className="fas fa-external-link text-lg"></i>
              </Link>
            </div>
          )}
        </ContactPreview.FooterActions>
      </ContactPreview>
    </div>
  )
}

// Persistent layout
// Docs: https://inertiajs.com/pages#persistent-layouts
ProIndex.layout = (page) => <MainLayout title="Find a PRO" children={page} />

export default ProIndex
